import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendAdminNewApplicationNotification } from '../../services/emailService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ConfirmRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, already_confirmed
  const [message, setMessage] = useState('');
  const [application, setApplication] = useState(null);
  const [dealershipInfo, setDealershipInfo] = useState(null);

  useEffect(() => {
    const confirmRegistration = async () => {
      const token = searchParams?.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Ungültiger Bestätigungslink. Bitte überprüfen Sie den Link aus Ihrer E-Mail.');
        return;
      }

      try {
        // Find application by confirmation token
        // Use left join to handle cases where dealership might not exist
        const { data: appData, error: fetchError } = await supabase
          ?.from('applications')
          ?.select(`
            *,
            dealerships (
              id,
              name,
              url_slug,
              status,
              voucher_value,
              voucher_currency
            )
          `)
          ?.eq('confirmation_token', token)
          ?.single();

        if (fetchError || !appData) {
          console.error('Application fetch error:', fetchError);
          setStatus('error');
          setMessage('Bestätigungslink nicht gefunden oder abgelaufen. Bitte kontaktieren Sie den Support.');
          return;
        }

        // Store dealership info for display
        if (appData?.dealerships) {
          setDealershipInfo({
            name: appData?.dealerships?.name,
            slug: appData?.dealerships?.url_slug,
            status: appData?.dealerships?.status,
            voucherValue: appData?.dealerships?.voucher_value,
            voucherCurrency: appData?.dealerships?.voucher_currency
          });
        }

        // Check if already confirmed
        if (appData?.confirmed_at) {
          setStatus('already_confirmed');
          setMessage('Ihre Registrierung wurde bereits bestätigt.');
          setApplication(appData);
          return;
        }

        // Update application status to 'bestätigt'
        const { data: updateData, error: updateError } = await supabase
          ?.from('applications')
          ?.update({
            application_status: 'bestätigt',
            confirmed_at: new Date()?.toISOString()
          })
          ?.eq('id', appData?.id)
          ?.select();

        if (updateError) {
          console.error('Application update error:', updateError);
          console.error('Update error details:', {
            message: updateError?.message,
            details: updateError?.details,
            hint: updateError?.hint,
            code: updateError?.code
          });
          throw updateError;
        }

        if (!updateData || updateData?.length === 0) {
          console.error('No data returned from update - application may not exist');
          throw new Error('Anwendung konnte nicht aktualisiert werden');
        }

        console.log('Application status successfully updated to bestätigt:', updateData);

        // Send admin notification for new confirmed application
        // Handle case where dealership might not exist or be inactive
        let dealershipName = 'Unbekannt';
        
        if (appData?.dealerships) {
          // Check if dealership exists and is active
          if (appData?.dealerships?.status === 'active') {
            dealershipName = appData?.dealerships?.name;
          } else {
            dealershipName = `${appData?.dealerships?.name} (Inaktiv)`;
            console.warn('Dealership is inactive:', appData?.dealerships);
          }
        } else if (appData?.dealership_id) {
          // Dealership ID exists but dealership not found - might be deleted
          dealershipName = 'Gelöscht';
          console.warn('Dealership not found for ID:', appData?.dealership_id);
        }
        
        try {
          await sendAdminNewApplicationNotification({
            applicationId: appData?.id,
            customerName: appData?.customer_name,
            customerEmail: appData?.customer_email,
            customerPhone: appData?.customer_phone,
            customerLicensePlate: appData?.customer_license_plate,
            dealershipName: dealershipName,
            chassisNumber: appData?.chassis_number
          });
        } catch (adminEmailError) {
          console.error('Error sending admin notification:', adminEmailError);
          // Don't fail the confirmation if admin email fails
        }

        setStatus('success');
        setMessage('Ihre Registrierung wurde erfolgreich bestätigt!');
        setApplication(appData);
      } catch (error) {
        console.error('Confirmation error:', error);
        setStatus('error');
        setMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      }
    };

    confirmRegistration();
  }, [searchParams]);

  const handleNavigateHome = () => {
    // If we have dealership info with a slug, navigate to that dealership's page
    if (dealershipInfo?.slug && dealershipInfo?.status === 'active') {
      navigate(`/${dealershipInfo?.slug}`);
    } else {
      // Otherwise go to homepage
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-card rounded-lg shadow-card p-8 md:p-12">
          {status === 'loading' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6 animate-pulse">
                <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Bestätigung wird verarbeitet...
              </h1>
              <p className="text-muted-foreground">
                Bitte warten Sie einen Moment.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-6">
                <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Registrierung bestätigt!
              </h1>
              <p className="text-muted-foreground mb-8">
                {message}
              </p>
              {dealershipInfo && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                  <p className="text-sm text-muted-foreground">
                    Vielen Dank für Ihre Registrierung über <strong className="text-foreground">{dealershipInfo?.name}</strong>
                  </p>
                </div>
              )}
              <div className="bg-muted rounded-lg p-6 mb-8 text-left">
                <h2 className="text-lg font-semibold text-foreground mb-4">Nächste Schritte:</h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">
                      Ihr Antrag wird nun von unserem Team geprüft
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="Mail" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">
                      Sie erhalten eine E-Mail, sobald Ihr Antrag bearbeitet wurde
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Icon name="Gift" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">
                      Nach Genehmigung erhalten Sie Ihren {dealershipInfo?.voucherValue || 150}{dealershipInfo?.voucherCurrency || '€'} Wertgutschein
                    </span>
                  </li>
                </ul>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Home"
                iconPosition="left"
                onClick={handleNavigateHome}
              >
                {dealershipInfo?.slug && dealershipInfo?.status === 'active' ? 'Zurück zum Partner' : 'Zur Startseite'}
              </Button>
            </div>
          )}

          {status === 'already_confirmed' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-full mb-6">
                <Icon name="Info" size={32} color="var(--color-blue-500)" />
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Bereits bestätigt
              </h1>
              <p className="text-muted-foreground mb-8">
                {message}
              </p>
              {dealershipInfo && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                  <p className="text-sm text-muted-foreground">
                    Ihre Registrierung über <strong className="text-foreground">{dealershipInfo?.name}</strong>
                  </p>
                </div>
              )}
              <div className="bg-muted rounded-lg p-6 mb-8">
                <p className="text-sm text-muted-foreground">
                  Ihr Antrag wird bereits bearbeitet. Sie erhalten eine E-Mail, sobald es Updates gibt.
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Home"
                iconPosition="left"
                onClick={handleNavigateHome}
              >
                {dealershipInfo?.slug && dealershipInfo?.status === 'active' ? 'Zurück zum Partner' : 'Zur Startseite'}
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mb-6">
                <Icon name="AlertCircle" size={32} color="var(--color-error)" />
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Bestätigung fehlgeschlagen
              </h1>
              <p className="text-muted-foreground mb-8">
                {message}
              </p>
              <div className="bg-muted rounded-lg p-6 mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-3">Hilfe benötigt?</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Bitte kontaktieren Sie uns, wenn das Problem weiterhin besteht:
                </p>
                <div className="space-y-2">
                  <p className="text-sm text-foreground">
                    <strong>E-Mail:</strong> thg@gridbalance.de
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Home"
                iconPosition="left"
                onClick={() => navigate('/')}
              >
                Zur Startseite
              </Button>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            © 2026 Grid Balance UG. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmRegistration;